/**
 * Admin API: product configuration — the classic product list, the detail
 * view with positions, print areas and mockup templates, activation and
 * the Direct Sale surcharge of a position.
 */
import apiClient, { ApiResponse } from '../client';
import type {
  CalibrationSource,
  FrameCalibrationStatus,
  CarryOverAnchor,
  ProductClass,
} from '@designdeck/shared';
import type {
  MockupBlendMode,
  PrintArea,
  Product,
  ProductMockupTemplate,
  ProductPosition,
} from '../products';
import type {
  PositionDecorationMethodAssignment,
  ProductDecorationMethodPrice,
} from './decoration-methods';
import type { ReleaseChannelKey } from './product-prices';

/**
 * A mockup template as the admin endpoints answer it: every template
 * whatever its calibration status, plus who calibrated it and when. The
 * frame on a template is `templateRect` of the print area's millimetres
 * and this calibration; the legacy pixel mirror is never read.
 */
export interface AdminMockupTemplate extends ProductMockupTemplate {
  calibrationSource?: CalibrationSource | null;
  calibratedAt?: string | null;
}

/**
 * Position as returned by the admin detail endpoint. Unlike the public list
 * shape, `decorationMethods` here is the full array of assignment rows.
 */
export interface AdminProductPosition extends Omit<
  ProductPosition,
  'decorationMethods'
> {
  decorationMethods?: PositionDecorationMethodAssignment[];
}

/**
 * Product as returned by the admin detail endpoint: positions carry their
 * full assignment rows and the product carries its price overrides.
 */
export interface AdminProduct extends Omit<
  Product,
  'positions' | 'mockupTemplates'
> {
  positions?: AdminProductPosition[];
  mockupTemplates?: AdminMockupTemplate[];
  decorationMethodPrices?: ProductDecorationMethodPrice[];
}

/**
 * Mockup layer types managed through the admin area.
 * Mirrors the backend MOCKUP_LAYER_TYPES.
 */
export type MockupLayerType =
  | 'base'
  | 'heathered'
  | 'texture'
  | 'shadow'
  | 'overlay'
  | 'cutout'
  | 'thumbnail';

export interface AdminProductListItem extends Product {
  positionCount: number;
  mockupTemplateCount: number;
  colorCount: number;
}

// ─── Frame-review walkthrough ─────────────────────────────────────

/** When a product's frame was last saved, relative to a day window. */
export const FRAME_CHANGE_FILTERS = ['never', 'within', 'before'] as const;
export type FrameChangeFilter = (typeof FRAME_CHANGE_FILTERS)[number];

/**
 * Calibration cases the walkthrough can be narrowed to: `open` steps
 * through every template still to calibrate (default or not), the other
 * two narrow by the products' default templates.
 */
export const FRAME_CALIBRATION_FILTERS = [
  'open',
  'uncalibrated',
  'inconsistent',
] as const;
export type FrameCalibrationFilter = (typeof FRAME_CALIBRATION_FILTERS)[number];

export interface FrameReviewQueueQuery {
  isActive?: boolean;
  search?: string;
  changed?: FrameChangeFilter;
  days?: number;
  calibration?: FrameCalibrationFilter;
}

/** One product of the frame-review queue, as the API answers it. */
export interface FrameReviewQueueItem {
  id: string;
  name: string;
  artNumber: string | null;
  isActive: boolean;
  mockupTemplateCount: number;
  /** ISO timestamp of the newest frame save; null = never saved by hand. */
  frameChangedAt: string | null;
  /** Worst calibration status of the product's default templates. */
  calibration: FrameCalibrationStatus;
  /**
   * Templates of any kind still to calibrate (UNCALIBRATED, NEEDS_REVIEW),
   * only those with a matching position.
   */
  templatesToCalibrate: number;
  /** Templates whose positionKey names no position: a data error. */
  templatesWithoutPosition?: number;
}

export interface FrameReviewQueueResult {
  items: FrameReviewQueueItem[];
  total: number;
}

/**
 * The queue of the frame-review walkthrough: products with mockups, in
 * catalogue order, narrowed by the walkthrough's filters.
 */
export async function getFrameReviewQueue(
  query: FrameReviewQueueQuery = {}
): Promise<FrameReviewQueueResult> {
  const params = new URLSearchParams();
  if (query.isActive !== undefined) {
    params.append('isActive', String(query.isActive));
  }
  if (query.search) params.append('search', query.search);
  if (query.changed) params.append('changed', query.changed);
  if (query.days !== undefined) params.append('days', String(query.days));
  if (query.calibration) params.append('calibration', query.calibration);
  const response = await apiClient.get<ApiResponse<FrameReviewQueueResult>>(
    `/admin/products/frame-review?${params.toString()}`
  );
  return response.data.data;
}

/**
 * The print area of a position, in millimetres only. Where it sits on a
 * mockup is each template's calibration (`calibrateTemplate`); changed
 * millimetres put every calibrated template of the position to review.
 */
export interface UpdatePrintAreaPayload {
  rotation?: number;
  maxWidthMm?: number | null;
  maxHeightMm?: number | null;
  offsetXCenterMm?: number | null;
  offsetYCollarMm?: number | null;
  /** Admin marker RUNDUM; null clears it (KLEIN is no longer accepted) */
  sizeTierOverride?: 'RUNDUM' | null;
  /** Area exception to the product carry-over percent (1-100); null inherits */
  carryOverPercent?: number | null;
  /** Area exception to the product anchor; null inherits */
  carryOverAnchor?: CarryOverAnchor | null;
  /** Area exception to the product top offset in mm; null inherits */
  carryOverTopOffsetMm?: number | null;
}

/** What a print area save answers besides the stored area. */
export interface UpdatePrintAreaResult extends PrintArea {
  /** Templates the save put to review (changed millimetres). */
  templatesNeedingReview: string[];
  /** Whether sellers may still pick the product (R13). */
  productSelectable: boolean;
  /** Set when this save made the product unselectable. */
  warning: string | null;
}

/**
 * The frame an admin drew on a template, in native pixels of its base
 * image. Never a height: it follows from the millimetres (R8).
 */
export interface CalibrateTemplatePayload {
  x: number;
  y: number;
  width: number;
  /** Accept the stored draft of a template to review (NEEDS_REVIEW). */
  confirmDraft?: boolean;
}

export interface CalibrateTemplateResult {
  template: AdminMockupTemplate;
  /** Whether sellers may pick the product after the calibration (R13). */
  productSelectable: boolean;
  /** Set when the product is (still) not selectable. */
  warning: string | null;
  /** Mockups re-rendered on the template; null without a design stack. */
  mockups: { queued: number; failed: number } | null;
}

/**
 * Product-level carry-over settings. Every field is optional; a field that
 * is sent as null clears the value (class unknown, percent off, anchor and
 * offset back to the defaults). Areas without an exception inherit them.
 */
export interface SetProductCarryOverPayload {
  productClass?: ProductClass | null;
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
}

/** How far a product's carry-over settings reach, before saving. */
export interface CarryOverImpact {
  /** Derived positions on this product whose placement the settings decide. */
  targetPositions: number;
  /** Positions of other products in designs whose master is this product. */
  sourcePositions: number;
  /** Distinct designs behind both counts. */
  designs: number;
  /** Print areas anywhere in the catalogue still carrying the retired KLEIN override. */
  kleinOverridesRemaining: number;
  /** Slugs of this product's areas that set their own carry-over values. */
  areaOverrides: string[];
}

export interface UpdateMockupTemplatePayload {
  name?: string;
  mockupType?: string;
  positionKey?: string;
  heatheredBlendMode?: MockupBlendMode;
  heatheredOpacity?: number;
  textureBlendMode?: MockupBlendMode;
  textureOpacity?: number;
  shadowBlendMode?: MockupBlendMode;
  shadowOpacity?: number;
  isDefault?: boolean;
  isCreatorEditable?: boolean;
  /** Render order of the layer stack, bottom → top */
  layerOrder?: string[];
}

export interface CreateMockupTemplatePayload {
  name: string;
  positionKey: string;
  mockupType?: string;
  file: File;
}

export interface AdminProductQuery {
  categoryId?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export interface AdminProductListResult {
  data: AdminProductListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

/**
 * Get products for admin configuration (paginated, includes inactive)
 */
export async function getAdminProducts(
  query?: AdminProductQuery
): Promise<AdminProductListResult> {
  const params = new URLSearchParams();
  if (query?.categoryId) params.append('categoryId', query.categoryId);
  if (query?.search) params.append('search', query.search);
  if (query?.page) params.append('page', String(query.page));
  if (query?.limit) params.append('limit', String(query.limit));

  const response = await apiClient.get<ApiResponse<AdminProductListResult>>(
    `/admin/products?${params.toString()}`
  );
  return response.data.data;
}

/** Answer of `PATCH /admin/products/:id/active`. */
export type SetProductActiveResult = Product & {
  /**
   * Channels the switch from off to on released the product on; empty
   * for every other call.
   */
  channelsReleased: ReleaseChannelKey[];
};

/**
 * Activate or deactivate a product. Switching it on from off releases it
 * on every admin channel (see `channelsReleased`).
 */
export async function setProductActive(
  productId: string,
  isActive: boolean
): Promise<SetProductActiveResult> {
  const response = await apiClient.patch<ApiResponse<SetProductActiveResult>>(
    `/admin/products/${productId}/active`,
    { isActive }
  );
  return response.data.data;
}

/**
 * Set the carry-over class, percent, anchor and top offset of a product.
 * Takes effect on cards and in the editor at once; stored mockups and
 * listings only after a re-render by the operator.
 */
export async function setProductCarryOver(
  productId: string,
  payload: SetProductCarryOverPayload
): Promise<Product> {
  const response = await apiClient.patch<ApiResponse<Product>>(
    `/admin/products/${productId}/carry-over`,
    payload
  );
  return response.data.data;
}

/** Positions and designs a product's carry-over settings reach. */
export async function getCarryOverImpact(
  productId: string
): Promise<CarryOverImpact> {
  const response = await apiClient.get<ApiResponse<CarryOverImpact>>(
    `/admin/products/${productId}/carry-over-impact`
  );
  return response.data.data;
}

/**
 * Mark or unmark a product as end of life (phased out, still sold)
 */
export async function setProductEndOfLife(
  productId: string,
  endOfLife: boolean
): Promise<{ id: string; endOfLifeAt: string | null }> {
  const response = await apiClient.patch<
    ApiResponse<{ id: string; endOfLifeAt: string | null }>
  >(`/admin/products/${productId}/end-of-life`, { endOfLife });
  return response.data.data;
}

/**
 * Get a single product with all positions, print areas and mockup templates
 */
export async function getAdminProduct(id: string): Promise<AdminProduct> {
  const response = await apiClient.get<ApiResponse<AdminProduct>>(
    `/admin/products/${id}`
  );
  return response.data.data;
}

/**
 * Create or update the print area (design area) of a position
 */
export async function updatePrintArea(
  positionId: string,
  payload: UpdatePrintAreaPayload
): Promise<UpdatePrintAreaResult> {
  const response = await apiClient.put<ApiResponse<UpdatePrintAreaResult>>(
    `/admin/products/positions/${positionId}/print-area`,
    payload
  );
  return response.data.data;
}

/**
 * Calibrate a mockup template (default or not) from the frame drawn on it.
 * The server derives pixel scale and anchors; the height follows from the
 * print area's millimetres. 422 names the missing millimetre field.
 */
export async function calibrateTemplate(
  productId: string,
  templateId: string,
  payload: CalibrateTemplatePayload
): Promise<CalibrateTemplateResult> {
  const response = await apiClient.put<ApiResponse<CalibrateTemplateResult>>(
    `/admin/products/${productId}/templates/${templateId}/calibration`,
    payload
  );
  return response.data.data;
}

/**
 * Set or clear the Direct Sale surcharge of a position.
 *
 * Pass null to mark the position unpriced, which removes it from the Direct
 * Sale flow. 0 is a real price (free decoration) and behaves differently.
 * The Seller flow ignores this value either way.
 */
export async function setPositionSurcharge(
  positionId: string,
  printSurcharge: number | null
): Promise<ProductPosition> {
  const response = await apiClient.put<ApiResponse<ProductPosition>>(
    `/admin/products/positions/${positionId}/surcharge`,
    { printSurcharge }
  );
  return response.data.data;
}

/**
 * Create a mockup template with its base image
 */
export async function createMockupTemplate(
  productId: string,
  payload: CreateMockupTemplatePayload
): Promise<ProductMockupTemplate> {
  const formData = new FormData();
  formData.append('file', payload.file);
  formData.append('name', payload.name);
  formData.append('positionKey', payload.positionKey);
  if (payload.mockupType) formData.append('mockupType', payload.mockupType);

  const response = await apiClient.post<ApiResponse<ProductMockupTemplate>>(
    `/admin/products/${productId}/mockup-templates`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.data;
}

/**
 * Update mockup template settings (placement, blend modes, flags)
 */
export async function updateMockupTemplate(
  templateId: string,
  payload: UpdateMockupTemplatePayload
): Promise<ProductMockupTemplate> {
  const response = await apiClient.patch<ApiResponse<ProductMockupTemplate>>(
    `/admin/products/mockup-templates/${templateId}`,
    payload
  );
  return response.data.data;
}

/**
 * Delete a mockup template
 */
export async function deleteMockupTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/admin/products/mockup-templates/${templateId}`);
}

/**
 * Upload or replace a mockup layer image
 */
export async function uploadMockupLayer(
  templateId: string,
  layerType: MockupLayerType,
  file: File
): Promise<ProductMockupTemplate> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post<ApiResponse<ProductMockupTemplate>>(
    `/admin/products/mockup-templates/${templateId}/layers/${layerType}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return response.data.data;
}

/**
 * Remove an optional mockup layer image (not allowed for base)
 */
export async function removeMockupLayer(
  templateId: string,
  layerType: MockupLayerType
): Promise<ProductMockupTemplate> {
  const response = await apiClient.delete<ApiResponse<ProductMockupTemplate>>(
    `/admin/products/mockup-templates/${templateId}/layers/${layerType}`
  );
  return response.data.data;
}
